import { API_ENDPOINTS } from '../config/api';
import { apiHelpers } from './normalizedApiClient';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';
import { repuestoTransforms } from '../utils/dataTransforms';

/**
 * Servicio para gestión de repuestos usando la nueva API backend
 * Requiere sucursal_id para todas las operaciones relevantes
 */

/**
 * Obtiene todos los repuestos filtrados por sucursal
 * @param {string} sucursalId - ID de la sucursal (requerido)
 * @param {Object} filtros - Filtros adicionales (opcional)
 * @returns {Promise<Array>} Lista de repuestos
 */
export const getRepuestos = async (sucursalId = null, filtros = {}) => {
  try {
    //console.log('⚙️ [RepuestosService] Obteniendo datos');

    
    const params = new URLSearchParams();
    
    if (sucursalId) {
      params.append('sucursal_id', sucursalId);
    }
    
    // Agregar filtros adicionales
    if (filtros.categoria_id) {
      params.append('categoria_id', filtros.categoria_id);
    }
    
    if (filtros.activo !== undefined) {
      params.append('activo', filtros.activo);
    }
    
    if (filtros.stock_bajo) {
      params.append('stock_bajo', 'true');
    }
    
    const url = `${API_ENDPOINTS.REPUESTOS.BASE}?${params.toString()}`;

    
    const response = await apiHelpers.repuestos.get(url);

    
    // El backend ahora devuelve directamente el array
    const repuestosArray = response?.data || [];
    
    // Verificar que tengamos un array
    if (!Array.isArray(repuestosArray)) {
      console.error('❌ getRepuestos - response.data no es un array:', response?.data);
      return [];
    }
    
    // Transformar datos para compatibilidad con componentes existentes
    const repuestosTransformados = repuestosArray.map(repuesto => 
      repuestoTransforms.toFrontend(repuesto)
    );
    

    return repuestosTransformados;
  } catch (error) {
    console.error('❌ Error en getRepuestos:', error);
    // En caso de error 500, devolver array vacío para que el componente no falle
    if (error.response?.status === 500) {
      console.warn('⚠️ getRepuestos - Error 500, devolviendo array vacío');
      return [];
    }
    throw error;
  }
};

/**
 * Obtiene repuestos con stock bajo
 * @param {string} sucursalId - ID de la sucursal (requerido)
 * @returns {Promise<Array>} Lista de repuestos con stock bajo
 */
export const getRepuestosStockBajo = async (sucursalId) => {
  try {
    //console.log('⚙️ [RepuestosService] Obteniendo datos');

    
    if (!sucursalId) {

      return [];
    }
    
    return await getRepuestos(sucursalId, { stock_bajo: true });
  } catch (error) {
    console.error('❌ Error en getRepuestosStockBajo:', error);
    return [];
  }
};

/**
 * Crea un nuevo repuesto
 * @param {Object} repuesto - Datos del repuesto a crear
 * @returns {Promise<Object>} Repuesto creado
 */
export const createRepuesto = async (repuesto) => {
  try {
    //console.log('⚙️ [RepuestosService] Creando registro');

    
    // Transformar datos al formato del backend
    const repuestoData = repuestoTransforms.toBackend(repuesto);
    
    const response = await apiHelpers.repuestos.post(API_ENDPOINTS.REPUESTOS.BASE, repuestoData);

    
    // El backend ahora devuelve directamente el objeto
    // Transformar respuesta para compatibilidad
    return repuestoTransforms.toFrontend(response?.data);
  } catch (error) {
    console.error('❌ Error en createRepuesto:', error);
    throw error;
  }
};

/**
 * Actualiza un repuesto existente
 * @param {string|number} id - ID del repuesto
 * @param {Object} repuesto - Datos actualizados del repuesto
 * @returns {Promise<Object>} Repuesto actualizado
 */
export const updateRepuesto = async (id, repuesto) => {
  try {
    //console.log('⚙️ [RepuestosService] Actualizando registro');

    
    // Transformar datos al formato del backend
    const repuestoData = repuestoTransforms.toBackend(repuesto);
    
    const response = await apiHelpers.repuestos.put(`${API_ENDPOINTS.REPUESTOS.BASE}/${id}`, repuestoData);

    
    // El backend ahora devuelve directamente el objeto
    // Transformar respuesta para compatibilidad
    return repuestoTransforms.toFrontend(response?.data);
  } catch (error) {
    console.error('❌ Error en updateRepuesto:', error);
    throw error;
  }
};

/**
 * Cambia el estado activo/inactivo de un repuesto
 * @param {string|number} id - ID del repuesto
 * @param {boolean} activo - Nuevo estado activo
 * @returns {Promise<Object>} Repuesto actualizado
 */
export const toggleRepuestoActive = async (id, activo) => {
  try {
    //console.log('⚙️ [RepuestosService] Procesando operación');
    //console.log('📦 toggleRepuestoActive - ID:', id, 'activo:', activo);
    
    return await updateRepuesto(id, { 
      activo: activo,
      Disponible: activo 
    });
  } catch (error) {
    console.error('❌ Error en toggleRepuestoActive:', error);
    throw error;
  }
};

/**
 * Elimina (desactiva) un repuesto
 * @param {string|number} id - ID del repuesto a eliminar
 * @returns {Promise<string>} ID del repuesto eliminado
 */
export const deleteRepuesto = async (id) => {
  try {
    //console.log('⚙️ [RepuestosService] Eliminando registro');
    //console.log('📦 deleteRepuesto - Iniciando para ID:', id);
    
    await apiHelpers.repuestos.delete(`${API_ENDPOINTS.REPUESTOS.BASE}/${id}`);
    //console.log('📦 deleteRepuesto - Repuesto eliminado:', id);
    
    return id;
  } catch (error) {
    console.error('❌ Error en deleteRepuesto:', error);
    throw error;
  }
};

/**
 * Obtiene un repuesto específico por ID
 * @param {string|number} id - ID del repuesto
 * @returns {Promise<Object>} Repuesto encontrado
 */
export const getRepuestoById = async (id) => {
  try {
    //console.log('⚙️ [RepuestosService] Obteniendo datos');
    //console.log('📦 getRepuestoById - Iniciando para ID:', id);
    
    const response = await apiHelpers.repuestos.get(`${API_ENDPOINTS.REPUESTOS.BASE}/${id}`);
    //console.log('📦 getRepuestoById - Repuesto encontrado:', response?.data);
    
    // El backend ahora devuelve directamente el objeto
    // Transformar respuesta para compatibilidad
    return repuestoTransforms.toFrontend(response?.data);
  } catch (error) {
    console.error('❌ Error en getRepuestoById:', error);
    throw error;
  }
};

/**
 * Busca repuestos por término
 * @param {string} termino - Término de búsqueda
 * @param {string} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} Lista de repuestos encontrados
 */
export const buscarRepuestos = async (termino, sucursalId = null) => {
  try {
    //console.log('⚙️ [RepuestosService] Procesando operación');
    //console.log('📦 buscarRepuestos - Término:', termino, 'sucursalId:', sucursalId);
    
    const params = new URLSearchParams();
    params.append('q', termino);
    
    if (sucursalId) {
      params.append('sucursal_id', sucursalId);
    }
    
    const url = `${API_ENDPOINTS.REPUESTOS.BASE}?${params.toString()}`;
    const response = await apiHelpers.repuestos.get(url);
    
    //console.log('📦 buscarRepuestos - Respuesta completa:', response);
    //console.log('📦 buscarRepuestos - Tipo de response.data:', typeof response?.data, Array.isArray(response?.data));
    //console.log('📦 buscarRepuestos - Encontrados:', response?.data?.length || 0, 'repuestos');
    
    // El backend ahora devuelve directamente el array
    const repuestosArray = response?.data || [];
    
    // Verificar que tengamos un array
    if (!Array.isArray(repuestosArray)) {
      console.error('❌ buscarRepuestos - response.data no es un array:', response?.data);
      return [];
    }
    
    // Transformar datos para compatibilidad
    const repuestosTransformados = repuestosArray.map(repuesto => 
      repuestoTransforms.toFrontend(repuesto)
    );
    
    return repuestosTransformados;
  } catch (error) {
    console.error('❌ Error en buscarRepuestos:', error);
    return [];
  }
};

/**
 * Actualiza el stock de un repuesto
 * @param {string|number} id - ID del repuesto
 * @param {Object} stockData - Datos del stock { operacion, cantidad, motivo }
 * @returns {Promise<Object>} Repuesto actualizado
 */
export const updateStockRepuesto = async (id, stockData) => {
  try {
    //console.log('⚙️ [RepuestosService] Actualizando registro');
    //console.log('📦 updateStockRepuesto - ID:', id, 'stockData:', stockData);
    
    const response = await apiHelpers.repuestos.put(`${API_ENDPOINTS.REPUESTOS.BASE}/${id}/stock`, stockData);
    //console.log('📦 updateStockRepuesto - Stock actualizado:', response?.data);
    
    // Transformar respuesta para compatibilidad
    return repuestoTransforms.toFrontend(response?.data);
  } catch (error) {
    console.error('❌ Error en updateStockRepuesto:', error);
    throw error;
  }
};

/**
 * Obtiene repuestos por categoría
 * @param {string|number} categoriaId - ID de la categoría
 * @param {string} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} Lista de repuestos de la categoría
 */
export const getRepuestosPorCategoria = async (categoriaId, sucursalId = null) => {
  try {
    //console.log('⚙️ [RepuestosService] Obteniendo datos');
    //console.log('📦 getRepuestosPorCategoria - CategoriaId:', categoriaId, 'sucursalId:', sucursalId);
    
    return await getRepuestos(sucursalId, { categoria_id: categoriaId });
  } catch (error) {
    console.error('❌ Error en getRepuestosPorCategoria:', error);
    return [];
  }
};

// Exportar todas las funciones como default también para compatibilidad
const repuestosApiService = {
  getRepuestos,
  getRepuestosStockBajo,
  createRepuesto,
  updateRepuesto,
  toggleRepuestoActive,
  deleteRepuesto,
  getRepuestoById,
  buscarRepuestos,
  updateStockRepuesto,
  getRepuestosPorCategoria
};

export default repuestosApiService;
